import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Plan configuration matching backend
const PLAN_CONFIG = {
  free: {
    name: "Free",
    features: {
      kyc: true,
      registro: true,
      screening_pep_ofac: true,
      captura_manual: true,
      logo_en_pdfs: true,
      reportes_pdf: true,
      reportes_xml_sat_uif: false,
      dashboard_cumplimiento: false,
      ocr_automatico: false,
      bitacora_audit_log: false,
      api_access: false,
      white_label: false,
    },
    available_roles: ["owner"],
  },
  basic: {
    name: "Basic",
    features: {
      kyc: true,
      registro: true,
      screening_pep_ofac: true,
      captura_manual: true,
      logo_en_pdfs: true,
      reportes_pdf: true,
      reportes_xml_sat_uif: false,
      dashboard_cumplimiento: false,
      ocr_automatico: false,
      bitacora_audit_log: false,
      api_access: false,
      white_label: false,
    },
    available_roles: ["owner"],
  },
  standard: {
    name: "Standard",
    features: {
      kyc: true,
      registro: true,
      screening_pep_ofac: true,
      captura_manual: true,
      logo_en_pdfs: true,
      reportes_pdf: true,
      reportes_xml_sat_uif: true,
      dashboard_cumplimiento: true,
      ocr_automatico: false,
      bitacora_audit_log: false,
      api_access: false,
      white_label: false,
    },
    available_roles: ["owner", "admin"],
  },
  pro: {
    name: "Pro",
    features: {
      kyc: true,
      registro: true,
      screening_pep_ofac: true,
      captura_manual: true,
      logo_en_pdfs: true,
      reportes_pdf: true,
      reportes_xml_sat_uif: true,
      dashboard_cumplimiento: true,
      ocr_automatico: true,
      bitacora_audit_log: true,
      api_access: true,
      white_label: false,
    },
    available_roles: ["owner", "admin", "compliance_officer", "capturista"],
  },
  enterprise: {
    name: "Enterprise",
    features: {
      kyc: true,
      registro: true,
      screening_pep_ofac: true,
      captura_manual: true,
      logo_en_pdfs: true,
      reportes_pdf: true,
      reportes_xml_sat_uif: true,
      dashboard_cumplimiento: true,
      ocr_automatico: true,
      bitacora_audit_log: true,
      api_access: true,
      white_label: true,
    },
    available_roles: ["owner", "admin", "compliance_officer", "capturista"],
  },
};

// Role permissions
const ROLE_PERMISSIONS = {
  owner: [
    "manage_org", "manage_billing", "manage_users", "view_customers", "create_customers",
    "edit_customers", "delete_customers", "verify_kyc", "approve_kyc", "view_operations",
    "create_operations", "run_screening", "view_screening_results", "upload_documents",
    "download_documents", "view_audit_logs", "generate_reports", "generate_xml_reports",
    "manage_privacy", "view_dashboard", "use_ocr", "use_api",
    "view_org_settings", "edit_org_settings",
  ],
  admin: [
    "manage_org", "manage_users", "view_customers", "create_customers", "edit_customers",
    "delete_customers", "verify_kyc", "approve_kyc", "view_operations", "create_operations",
    "run_screening", "view_screening_results", "upload_documents", "download_documents",
    "view_audit_logs", "generate_reports", "generate_xml_reports", "manage_privacy",
    "view_dashboard", "use_ocr", "use_api",
    "view_org_settings", "edit_org_settings",
  ],
  compliance_officer: [
    "view_customers", "create_customers", "edit_customers", "verify_kyc", "approve_kyc",
    "view_operations", "create_operations", "run_screening", "view_screening_results",
    "upload_documents", "download_documents", "view_audit_logs", "generate_reports",
    "generate_xml_reports", "view_dashboard", "use_ocr",
  ],
  capturista: [
    "view_customers", "create_customers", "edit_customers", "view_operations",
    "create_operations", "upload_documents", "view_dashboard",
  ],
  superadmin: ["*"], // All permissions
};

// Feature to required plan mapping
const FEATURE_REQUIRED_PLAN = {
  reportes_xml_sat_uif: "standard",
  dashboard_cumplimiento: "standard",
  ocr_automatico: "pro",
  bitacora_audit_log: "pro",
  api_access: "pro",
  white_label: "enterprise",
};

const OrganizationContext = createContext(null);

export function OrganizationProvider({ children }) {
  const { user } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrganization = useCallback(async () => {
    if (!user || user.role === "superadmin") {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const [orgRes, usageRes] = await Promise.all([
        axios.get(`${API}/organizations/my-org`, { withCredentials: true }),
        axios.get(`${API}/organizations/my-usage`, { withCredentials: true }),
      ]);
      setOrganization(orgRes.data);
      setUsage(usageRes.data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch organization:", err);
      setError(err.response?.data?.detail || "Error loading organization");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchOrganization();
    } else {
      setOrganization(null);
      setUsage(null);
      setLoading(false);
    }
  }, [user, fetchOrganization]);

  // ── Live refresh: pick up plan/limit changes made out-of-band
  // (most commonly: a SuperAdmin moving the org between plans). The
  // session token does NOT carry plan info — every backend request
  // reads ``db.organizations`` fresh — but React state, once
  // hydrated, stays put across SPA navigation. Without this listener
  // a user whose plan was just upgraded would keep seeing the old
  // gating until they hard-reload.
  //
  // We refresh whenever the tab regains focus or visibility, with a
  // 5s debounce so navigating quickly between tabs doesn't hammer
  // ``/organizations/my-org``. This is the same pattern most SaaS
  // dashboards use to surface external admin actions.
  useEffect(() => {
    if (!user || user.role === "superadmin") return;
    let lastRefresh = Date.now();
    const REFRESH_DEBOUNCE_MS = 5_000;
    const maybeRefresh = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastRefresh < REFRESH_DEBOUNCE_MS) return;
      lastRefresh = now;
      fetchOrganization();
    };
    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", maybeRefresh);
    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", maybeRefresh);
    };
  }, [user, fetchOrganization]);

  // Check if current plan has a feature
  const hasFeature = useCallback((feature) => {
    if (!user) return false;
    if (user.role === "superadmin") return true;
    
    const plan = organization?.plan || "free";
    const planConfig = PLAN_CONFIG[plan];
    return planConfig?.features?.[feature] ?? false;
  }, [user, organization]);

  // Check if user has permission (role-based)
  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    if (user.role === "superadmin") return true;
    
    const rolePerms = ROLE_PERMISSIONS[user.role] || [];
    return rolePerms.includes("*") || rolePerms.includes(permission);
  }, [user]);

  // Get the plan required for a feature
  const getRequiredPlan = useCallback((feature) => {
    return FEATURE_REQUIRED_PLAN[feature] || "enterprise";
  }, []);

  // Check if a limit is reached
  const isLimitReached = useCallback((limitType) => {
    if (!usage) return false;
    
    const usageData = usage.monthly_usage?.[limitType] || usage.account_usage?.[limitType];
    if (!usageData) return false;
    
    return usageData.current >= usageData.max;
  }, [usage]);

  // Get usage info for a limit
  const getUsageInfo = useCallback((limitType) => {
    if (!usage) return { current: 0, max: 0, percentage: 0 };
    
    const usageData = usage.monthly_usage?.[limitType] || usage.account_usage?.[limitType];
    if (!usageData) return { current: 0, max: 0, percentage: 0 };
    
    return {
      current: usageData.current,
      max: usageData.max,
      percentage: Math.min(100, Math.round((usageData.current / usageData.max) * 100)),
    };
  }, [usage]);

  const value = {
    organization,
    usage,
    loading,
    error,
    plan: organization?.plan || "free",
    planName: PLAN_CONFIG[organization?.plan]?.name || "Free",
    hasFeature,
    hasPermission,
    getRequiredPlan,
    isLimitReached,
    getUsageInfo,
    refetch: fetchOrganization,
    isSuperAdmin: user?.role === "superadmin",
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error("useOrganization must be used within OrganizationProvider");
  }
  return context;
};
